import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  GlobalStyles,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import { useAppState } from '../context/LoadTestContext';
import { downloadLoadTestExcel } from '../services/api';
import StepSummary from './StepSummary';
import LoadGraph from './graphs/LoadGraph';
import StrainGaugeGraph from './graphs/StrainGaugeGraph';


const SAVE_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'excel', label: 'Excel' },
];


function SaveOptionsMenu() {
  const {
    loadTestData,
    loading,
    loadTest,
    projectDetails,
    saveOptionsAnchor,
    setSaveOptionsAnchor,
  } = useAppState();
  const [pdfReportOpen, setPdfReportOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({
    pdf: false,
    excel: true,
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleSaveOptionsClose = () => {
    setSaveOptionsAnchor(null);
    setSaveError(null);
  };

  const handleOptionChange = (option) => {
    setSelectedOptions((currentOptions) => ({
      ...currentOptions,
      [option]: !currentOptions[option],
    }));
  };

  // The workbook is built by the server from the test's stored readings, so
  // it has the raw values as well as the converted ones, with the revision on
  // screen (the one being viewed on a past test, the one in use when live).
  const handleExcelSave = () => downloadLoadTestExcel(loadTest.id, loadTest.revision?.id);

  const handlePdfSave = () => {
    if (!selectedOptions.pdf || !Array.isArray(loadTestData) || loadTestData.length === 0) {
      return;
    }

    setPdfReportOpen(true);
  };

  const handleSave = async () => {
    setSaveError(null);
    if (selectedOptions.excel) {
      setSaving(true);
      try {
        await handleExcelSave();
      } catch (err) {
        setSaveError(err.message);
        return;
      } finally {
        setSaving(false);
      }
    }
    handleSaveOptionsClose();
    handlePdfSave();
  }

  const hasLoadTestData = Array.isArray(loadTestData) && loadTestData.length > 0;
  // Excel needs the test's record; it is missing only when the test tables
  // couldn't be read.
  const canSave = hasLoadTestData && (!selectedOptions.excel || Boolean(loadTest?.id))
    && (selectedOptions.excel || selectedOptions.pdf);

  return (
    <Menu
      anchorEl={saveOptionsAnchor}
      open={Boolean(saveOptionsAnchor)}
      onClose={handleSaveOptionsClose}
      disableScrollLock
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      MenuListProps={{
        'aria-labelledby': 'save-dashboard-button',
        dense: true,
      }}
    >
      <Typography
        variant='overline'
        color='text.secondary'
        sx={{ display: 'block', px: 2, pt: 0.5 }}
      >
        Save Options
      </Typography>

      {SAVE_OPTIONS.map(({ value, label }) => (
        <MenuItem
          key={value}
          onClick={() => handleOptionChange(value)}
          selected={selectedOptions[value]}
          sx={{ pl: 0.25 }}
        >
          <Checkbox
            checked={selectedOptions[value]}
            size='small'
            tabIndex={-1}
            disableRipple
            inputProps={{ 'aria-label': `Save as ${label}` }}
          />
          <ListItemText primary={label} />
        </MenuItem>

      ))}
      {saveError && (
        <Typography variant='caption' color='error' sx={{ display: 'block', px: 2, pt: 0.5, maxWidth: 220 }}>
          {saveError}
        </Typography>
      )}
      <Stack direction='row' justifyContent='right' sx={{ pt: 1, px: 1 }}>
        <Button
          size='small'
          fullWidth
          variant='contained'
          onClick={handleSave}
          disabled={loading || saving || !canSave}
          aria-label='Download selected save options'
        >
          {saving ? <CircularProgress size={20} color='inherit' /> : <ArrowRightAltIcon />}
        </Button>
      </Stack>
    </Menu>
  );
}

export default SaveOptionsMenu;
